import React, { useState } from 'react';
import { AutoAwesome as CoachIcon, Edit, ExpandMore,Mic, MicOff } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';

import { VoiceInstructionInput } from '../../notes/components/VoiceInstructionInput';
import { useEditNoteWithContext } from '../../notes/hooks/useEditNoteWithContext';
import type { JotCoachProviderQuestion } from '../store/hooks';

interface RulesAnalysis {
  overall_quality_score?: number;
  billing_support?: {
    proposed_primary_code?: string;
    supports_suggested_cpt?: boolean;
    confidence?: number;
    strengths?: string[];
    concerns?: string[];
    missing_elements_for_code?: string[];
  };
}

interface SessionData {
  rules_analysis?: RulesAnalysis;
}

interface SlimJotCoachOverviewProps {
  sessionData?: SessionData;
  noteId?: string;
  organizationId?: string | null;
  sectionCount?: number;
  providerQuestions?: JotCoachProviderQuestion[];
}

export const SlimJotCoachOverview: React.FC<SlimJotCoachOverviewProps> = ({
  sessionData,
  noteId,
  organizationId,
  sectionCount = 0,
  providerQuestions = [],
}) => {
  // Dictation state
  const [isExpanded, setIsExpanded] = useState(false);
  const [instructionText, setInstructionText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const { editNote, isLoading: isEditingNote } = useEditNoteWithContext();

  const hasTooManySections = sectionCount > 16;

  const handleDictationSubmit = async () => {
    if (!instructionText.trim() || !noteId) {
      return;
    }

    await editNote({
      note_id: noteId,
      organization_id: organizationId,
      text: instructionText.trim(),
    });

    setInstructionText('');
  };

  const handleVoiceTranscript = (transcript: string) => {
    setInstructionText(prev => (prev ? `${prev} ${transcript}` : transcript));
  };

  const handleRecordingToggle = () => {
    setIsRecording(!isRecording);
  };

  // Show up to 2 questions when expanded
  const displayQuestions = providerQuestions.slice(0, 2);

  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'primary.main',
        boxShadow: 2,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
        }}
      >
        <CoachIcon sx={{ color: 'primary.main', fontSize: 24 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem' }}>
          JotCoach Analysis
        </Typography>
      </Box>

      <CardContent sx={{ p: 2, pt: 0 }}>
        {/* Add Dictation Button */}
        {noteId && (
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            disabled={hasTooManySections}
            variant="outlined"
            endIcon={
              <ExpandMore
                sx={{
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            }
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                backgroundColor: 'action.hover',
              },
              '&:disabled': {
                borderColor: 'action.disabled',
                color: 'action.disabled',
              },
            }}
          >
            Do a quick dictation to update note?
          </Button>
        )}

        {/* Expandable Dictation Section */}
        {noteId && (
          <Collapse in={isExpanded}>
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              {/* Provider Questions */}
              {displayQuestions.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.75, display: 'block', fontSize: '0.7rem', fontWeight: 600 }}
                  >
                    Consider addressing:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {displayQuestions.map(question => (
                      <Box
                        key={question.id}
                        sx={{
                          bgcolor: 'background.paper',
                          px: 1,
                          py: 0.5,
                          borderRadius: 0.5,
                          border: '1px solid',
                          borderColor: 'info.light',
                          borderLeftWidth: 3,
                          borderLeftColor: 'info.main',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.7rem',
                            lineHeight: 1.4,
                            color: 'text.primary',
                          }}
                        >
                          {question.question}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Text Input with Microphone */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                <TextField
                  multiline
                  rows={2}
                  placeholder="Dictate your additions or edits..."
                  value={instructionText}
                  onChange={e => setInstructionText(e.target.value)}
                  disabled={isEditingNote || hasTooManySections}
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.875rem',
                    },
                  }}
                  variant="outlined"
                  size="small"
                />
                <IconButton
                  onClick={handleRecordingToggle}
                  disabled={isEditingNote || hasTooManySections}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    backgroundColor: isRecording ? 'error.main' : 'primary.main',
                    color: 'white',
                    mt: 0.5,
                    flexShrink: 0,
                    '&:hover': {
                      backgroundColor: isRecording ? 'error.dark' : 'primary.dark',
                    },
                  }}
                >
                  {isRecording ? <MicOff sx={{ fontSize: 24 }} /> : <Mic sx={{ fontSize: 24 }} />}
                </IconButton>
              </Box>

              {/* Hidden Voice Component */}
              <VoiceInstructionInput
                onTranscript={handleVoiceTranscript}
                disabled={isEditingNote || hasTooManySections}
                hidden
                externalToggle={isRecording}
                onRecordingChange={setIsRecording}
              />

              {/* Submit Button */}
              <Button
                variant="contained"
                onClick={handleDictationSubmit}
                disabled={!instructionText.trim() || isEditingNote || hasTooManySections}
                startIcon={<Edit />}
                size="small"
                sx={{ alignSelf: 'flex-start' }}
              >
                Update Note
              </Button>

              {hasTooManySections && (
                <Typography
                  variant="caption"
                  color="warning.main"
                  sx={{ fontSize: '0.7rem', mt: 0.5, display: 'block' }}
                >
                  Note has too many sections ({sectionCount}). Consider using a shorter template.
                </Typography>
              )}
            </Box>
          </Collapse>
        )}
      </CardContent>
    </Card>
  );
};
